import React, { useCallback, useMemo } from "react";
import classnames from "classnames";

import { DISPENSARY_PLACEHOLDER_PHOTO } from "@/constants/dispensary";
import {
  DispensaryV3DSA,
  DispensaryV3DSARanges,
} from "@/custom-types/DispensariesV3";
import { DispensaryServiceAreaRanges } from "@/custom-types/Dispensary";
import { formatMaxTimeEstimate } from "@/utils/formatMaxTimeEstimate";

import Image from "@/components/Image";

interface Props {
  /*
  Minimal duck type based on current inputs to this component:
  Dispensary, DispensaryV3, VariantWithDeliveryInfo

  exactDeliveryServiceArea and userServiceArea represent the same data, but are named
  differently coming from different sources.
  */
  dispensary: {
    deliveryServiceAreaRanges?:
      | null
      | DispensaryV3DSARanges
      | DispensaryServiceAreaRanges; // ranges not in VariantWithDeliveryInfo type, but values in data quack like these
    exactDeliveryServiceArea?: DispensaryV3DSA | null;
    logoUrl: string | null; // in theory could also be logoImage, but not seeing that in practice
    name: string;
    userServiceArea?: DispensaryV3DSA; // can't find a type def for this anywhere, but quacks like DispensaryV3DSA
  };

  /**
   * showCarouselInfo is an optional prop for displaying dispensary information on carousel cards.
   */
  showCarouselInfo?: boolean;
}

const DispensaryFulfillmentInfo: React.FC<Props> = ({
  dispensary,
  showCarouselInfo = false,
}) => {
  const {
    logoUrl,
    deliveryServiceAreaRanges,
    name: dispensaryName,
  } = dispensary || {};

  // data for a specific DSA could come in either of these props:
  const userServiceArea =
    dispensary.userServiceArea ?? dispensary.exactDeliveryServiceArea;

  const dispensaryImage = logoUrl || DISPENSARY_PLACEHOLDER_PHOTO;

  const formatDispensaryMaxDeliveryTime = useCallback(
    (format: "verbose" | "short" | "mobile") =>
      userServiceArea
        ? formatMaxTimeEstimate(
            Number(userServiceArea?.deliveryDurationMaximum),
            format,
          )
        : deliveryServiceAreaRanges
          ? formatMaxTimeEstimate(
              Number(deliveryServiceAreaRanges?.maxDeliveryDurationMaximum),
              format,
            )
          : null,
    [
      userServiceArea?.deliveryDurationMaximum,
      deliveryServiceAreaRanges?.maxDeliveryDurationMaximum,
    ],
  );

  const dispensaryOrderMinimum = useMemo(
    () =>
      userServiceArea
        ? `$${userServiceArea.orderMinimum} min`
        : deliveryServiceAreaRanges
          ? `$${deliveryServiceAreaRanges?.maxOrderMinimum} min`
          : null,
    [userServiceArea?.orderMinimum, deliveryServiceAreaRanges?.maxOrderMinimum],
  );

  return (
    <>
      {(userServiceArea || deliveryServiceAreaRanges) && (
        <>
          <div
            className={classnames("gap-sm mt-xs", {
              flex: showCarouselInfo,
              "hidden md:flex": !showCarouselInfo,
            })}
            data-testid="menu-item-card__fulfillment"
          >
            <div
              className="flex-shrink-0 border rounded border-grey-44"
              style={{ height: "24px", width: "24px" }}
            >
              <Image
                src={dispensaryImage}
                alt={dispensaryName}
                sizes={[24]}
                className="rounded"
              />
            </div>

            <div
              className="flex flex-col truncate font-bold text-xs"
              data-testid="menu-item-card__delivery-info"
            >
              <span className="truncate">
                <div className="truncate">{dispensaryName}</div>
                {formatDispensaryMaxDeliveryTime("short")}
                <span className="leading-none divider-dot" />
                {dispensaryOrderMinimum}
              </span>
            </div>
          </div>
          {!showCarouselInfo && (
            <div
              className="md:hidden mt-xs p-xs bg-leafly-white rounded w-full"
              data-testid="menu-item-card__fulfillment-mobile"
            >
              <div
                className="flex items-center font-bold text-xs"
                data-testid="menu-item-card__delivery-info-mobile"
              >
                <span className="mr-xs" style={{ height: 16, width: 16 }}>
                  <Image
                    aria-hidden
                    src="https://leafly-public.imgix.net/web-web/DeliveryScooterIcon.svg"
                    sizes={[16]}
                    ratio="square"
                    alt="scooter icon"
                  />
                </span>
                <span>
                  {formatDispensaryMaxDeliveryTime("mobile")}
                  <span className="divider-dot" />
                  {dispensaryOrderMinimum}
                </span>
              </div>
            </div>
          )}
          <style jsx>{`
            .divider-dot::after {
              content: "•";
              margin: 0px 4px;
            }
          `}</style>
        </>
      )}
    </>
  );
};

export default DispensaryFulfillmentInfo;
